import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { SongAnalysisService } from '../../services/songAnalysisService';
import SearchSection from './components/SearchSection';
import SongTable from './components/SongTable';

const SongManagementPage = () => {
  const { user, userProfile, isSuperUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    songTitle: '',
    musical: '',
    editor: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  useEffect(() => {
    if (user && userProfile && !isSuperUser()) {
      navigate('/');
      return;
    }
    
    if (user && userProfile) {
      loadSongs();
    }
  }, [user, userProfile, navigate, isSuperUser]);

  useEffect(() => {
    filterAndSortSongs();
  }, [songs, searchFilters, sortConfig]);

  const loadSongs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error } = await SongAnalysisService?.getSongAnalyses();
      
      if (error) {
        setError('Failed to load songs');
      } else {
        setSongs(data || []);
      }
    } catch (err) {
      setError('Failed to load songs');
      console.error('Error loading songs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortSongs = () => {
    let filtered = [...songs];

    // Apply search filters
    if (searchFilters?.songTitle?.trim()) {
      filtered = filtered?.filter(song => 
        song?.overview?.songTitle?.toLowerCase()?.includes(searchFilters?.songTitle?.toLowerCase())
      );
    }

    if (searchFilters?.musical?.trim()) {
      filtered = filtered?.filter(song => 
        song?.overview?.musical?.toLowerCase()?.includes(searchFilters?.musical?.toLowerCase())
      );
    }

    if (searchFilters?.editor?.trim()) {
      filtered = filtered?.filter(song => 
        song?.team_member?.toLowerCase()?.includes(searchFilters?.editor?.toLowerCase())
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig?.key) {
        case 'song_title':
          aValue = a?.overview?.songTitle || '';
          bValue = b?.overview?.songTitle || '';
          break;
        case 'musical':
          aValue = a?.overview?.musical || '';
          bValue = b?.overview?.musical || '';
          break;
        case 'editor':
          aValue = a?.team_member || '';
          bValue = b?.team_member || '';
          break;
        case 'created_at':
          aValue = new Date(a?.created_at);
          bValue = new Date(b?.created_at);
          break;
        case 'updated_at':
          aValue = new Date(a?.updated_at);
          bValue = new Date(b?.updated_at);
          break;
        default:
          aValue = a?.[sortConfig?.key] || '';
          bValue = b?.[sortConfig?.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredSongs(filtered);
  };

  const handleSearchChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleViewSong = (songId) => {
    navigate(`/song-detail-page?id=${songId}`);
  };

  const handleEditSong = (songId) => {
    navigate(`/song-addition-form/${songId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="lg" className="text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header 
        title="Song Management" 
        user={userProfile}
        onLogout={handleLogout}
        actions={[
          <Button 
            key="back"
            variant="outline" 
            onClick={() => navigate('/admin')}
            className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
          >
            Back to Admin
          </Button>
        ]}
      />
      
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header Stats */}
        <div className="mb-6">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-2">Database Management</h2>
            <p className="text-blue-100">
              Total Songs: <span className="font-semibold text-white">{songs?.length || 0}</span>
              {filteredSongs?.length !== songs?.length && (
                <span className="ml-4">
                  Filtered: <span className="font-semibold text-white">{filteredSongs?.length || 0}</span>
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Search Section */}
        <SearchSection 
          searchFilters={searchFilters}
          onSearchChange={handleSearchChange}
        />

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Song Table */}
        <SongTable 
          songs={filteredSongs}
          sortConfig={sortConfig}
          onSort={handleSort}
          onView={handleViewSong}
          onEdit={handleEditSong}
        />
      </div>
    </div>
  );
};

export default SongManagementPage;